import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import moment from 'moment-timezone';

import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import type { IListCasesParams } from '../../../../../services/cases/listAllCases';
import { listAllClientTags } from '../../../../../services/identity/listAllClientTags';
import { getClientDivisionOptions } from '../../../../../services/identity/clientDivisionOptions';
import { ClientTag, UserProfile, UserRoleName } from '../../../../../services/identity/types/UserProfile';
import useCases from '../../../../../hooks/useCases';
import useSubscribe, { Subscriber } from '../../../../../hooks/useSubscribe';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/useReduxHook';
import { selectAuth } from '@brands/store/selectors/auth';
import { ColumnVisibilityKey } from '@brands/store/slices/columnVisibilitySlice';
import {
  TablePaginationKey,
  setRowsPerPage as setPersistedRowsPerPage,
} from '@brands/store/slices/tablePaginationSlice';
import { calculateProviderCaseDue } from '@brands/Utils/calculateCaseDue';
import { formatDate } from '@brands/Utils/formatDate';
import { getFriendlyName } from '@brands/Utils/getFriendlyName';
import { getStatusColorStyles } from '@brands/Utils/getColorForStatus';
import { getTextForStatus } from '@brands/Utils/getTextForStatus';
import { getCollectorDisplayName } from '@brands/Dashboard/ConsultationPage/utils/consultationHelpers';
import { COLLECTOR_CASE_CREATION_BASE_PATH } from '@brands/Utils/reviewProfileBasePath';
import {
  getSessionNumber,
  getSessionString,
  readSessionTableState,
  writeSessionTableState,
} from '@brands/Utils/sessionTableState';
import MinimalDateRangePicker from '@brands/Components/MinimalDateRangePicker/MinimalDateRangePicker';
import { caseStatusOptions, allCaseTypeOptions } from '@brands/Utils/selectOptions';
import { Loading } from '../../../../../Components/LoadingSpinner/Loading';
import useWindowSize from '../../../../../hooks/useWindowSize';
import type { ICase } from '../../../../../services/cases/types/ICase';
import CADataGrid, { ColumnDef, EllipsisCell, columnWidthPx } from '../../../CareAdvisor/Components/CADataGrid';
import styles from '../../styles.module.scss';
import { CaseType, Status } from '@brands/services/cases/types/ICase';

const COLUMNS = [
  { key: 'created_at', label: 'Requested date' },
  { key: 'due_date', label: 'Due date' },
  { key: 'case_type', label: 'Case type' },
  { key: 'id', label: 'Case ID' },
  { key: 'patient', label: 'Patient' },
  { key: 'provider', label: 'Provider' },
  { key: 'care_advisor', label: 'Care Advisor' },
  { key: 'collector', label: 'Collector' },
  { key: 'client_tag', label: 'Client tags' },
  { key: 'client_division', label: 'Division' },
  { key: 'status', label: 'Status' },
] as const;
const ROWS_PER_PAGE_OPTIONS = [10, 25, 100];
const DEFAULT_ROWS_PER_PAGE = 10;

type ColumnKey = (typeof COLUMNS)[number]['key'];
type DateFilterKey = 'created_at' | 'due_date';

const SORT_BY_MAP: Record<ColumnKey, string> = {
  created_at: 'created_at',
  due_date: 'due_date',
  case_type: 'type',
  id: 'case_id',
  patient: 'patient_full_name',
  provider: 'provider_full_name',
  care_advisor: 'care_advisor_full_name',
  collector: 'collector_full_name',
  client_tag: 'client_tag',
  // No server-side case sort by division yet; this column is rendered non-sortable below,
  // so this entry only exists to satisfy the Record<ColumnKey, string> type.
  client_division: 'client_division',
  status: 'status',
};

// Completed and canceled cases stay reachable by filtering, just not shown
// on the unfiltered landing view.
const DEFAULT_STATUS_PARAM = caseStatusOptions
  .map((o) => o.value)
  .filter((v) => v !== Status.Completed && v !== Status.Canceled);

const SESSION_STATE_KEY = 'dentistry-one:collector:cases';

const filterLabelSx = { fontSize: 12, fontWeight: 400 };

const cleanFilters = (raw: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(raw).filter(([_, v]) => !(v == null || v === '' || (Array.isArray(v) && !v.length))),
  );

const isColumnKey = (value: string | null): value is ColumnKey =>
  COLUMNS.some((column) => column.key === value);

function buildColumnWidths(
  rows: ICase[],
  roleName: string,
  userInfo: UserProfile | null | undefined,
): Record<ColumnKey, number> {
  const out = {} as Record<ColumnKey, number>;
  const stringsFor = (key: ColumnKey): string[] =>
    rows.map((row) => {
      switch (key) {
        case 'created_at': return formatDate(row.created_at);
        case 'due_date':
          return calculateProviderCaseDue(row.type, row.started_at, row.video_conference_date || '', row.due_date);
        case 'case_type': return getFriendlyName(row.type);
        case 'id': return String(row.id);
        case 'patient': return row.patient?.full_name ?? '-';
        case 'provider': return row.provider?.full_name ?? '-';
        case 'care_advisor': return row.care_advisor?.full_name ?? '-';
        case 'collector':
          return userInfo ? getCollectorDisplayName(row, userInfo) : '-';
        case 'client_tag': return row.client?.name ?? '-';
        case 'client_division': return row.client_division?.name ?? '-';
        case 'status': return getTextForStatus(roleName, row.type, row.status);
        default: return '';
      }
    });
  COLUMNS.forEach((c) => {
    const values = stringsFor(c.key);
    out[c.key] = columnWidthPx(c.label, values.length ? values : ['—']);
  });
  return out;
}

const Appointments = (): JSX.Element => {
  const { userInfo } = useAppSelector(selectAuth);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const savedState = useMemo(() => readSessionTableState(SESSION_STATE_KEY), []);
  const rowsPerPageHydratedRef = useRef(false);
  const roleNavigate = useRoleNavigate();
  const { subscribe, unsubscribe } = useSubscribe();
  const timezone = moment.tz.guess();
  const { width } = useWindowSize();
  const isMobile = width <= 768;

  // "View full history" on the consultation page deep-links here with a
  // ?patient_full_name= query param. Seed the patient filter from it.
  const [searchParams] = useSearchParams();
  const initialPatientFilter = searchParams.get('patient_full_name')?.trim() ?? '';
  const incomingPatientPersistedRef = useRef(false);

  const rowsPerPageOverride = useAppSelector(
    (s) => s.tablePagination[TablePaginationKey.CollectorCases] ?? null,
  );
  const rowsPerPage = ROWS_PER_PAGE_OPTIONS.includes(rowsPerPageOverride ?? -1)
    ? (rowsPerPageOverride as number)
    : DEFAULT_ROWS_PER_PAGE;

  useEffect(() => {
    if (rowsPerPageOverride === null || ROWS_PER_PAGE_OPTIONS.includes(rowsPerPageOverride)) return;
    dispatch(
      setPersistedRowsPerPage({
        tableKey: TablePaginationKey.CollectorCases,
        rowsPerPage: DEFAULT_ROWS_PER_PAGE,
      }),
    );
  }, [dispatch, rowsPerPageOverride]);

  const [filtersReady, setFiltersReady] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>(() => {
    const { page, rowsPerPage: _rowsPerPage, sort_by, sort_direction, ...filters } = savedState;
    return initialPatientFilter ? { ...filters, patient_full_name: initialPatientFilter } : filters;
  });
  const [selectedRangeCreatedAt, setSelectedRangeCreatedAt] = useState<[Date, Date] | null>(null);
  const [selectedRangeDueDate, setSelectedRangeDueDate] = useState<[Date, Date] | null>(null);
  const [sortBy, setSortBy] = useState<ColumnKey | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);
  const [targetPageIndex, setTargetPageIndex] = useState(0);
  const [allClientTags, setAllClientTags] = useState<ClientTag[]>([]);

  useEffect(() => {
    listAllClientTags(UserRoleName.Collector)
      .then(({ nodes }) => setAllClientTags(nodes.filter((tag) => Boolean(tag.code))))
      .catch(() => setAllClientTags([]));
  }, []);

  const clientTagOptions = useMemo(
    () => allClientTags.map((tag) => ({ value: tag.code as string, label: tag.name })),
    [allClientTags],
  );

  const selectedClientTagIdsForDivision = useMemo(() => {
    const selectedCodes = (filterValues.client_tag as string[] | undefined) || [];
    return allClientTags.filter((tag) => selectedCodes.includes(tag.code || '')).map((tag) => tag.id);
  }, [allClientTags, filterValues.client_tag]);

  const divisionFilterOptions = useMemo(
    () => getClientDivisionOptions(allClientTags, selectedClientTagIdsForDivision),
    [allClientTags, selectedClientTagIdsForDivision],
  );

  useEffect(() => {
    if (rowsPerPageHydratedRef.current) return;
    rowsPerPageHydratedRef.current = true;
    const rowsPerPageFromSession = getSessionNumber(savedState, 'rowsPerPage', rowsPerPage);
    const pageFromSession = getSessionNumber(savedState, 'page', 1);
    const sortByFromSession = getSessionString(savedState, 'sort_by');
    const sortDirectionFromSession = getSessionString(savedState, 'sort_direction');
    setFiltersReady(true);
    setTargetPageIndex(initialPatientFilter ? 0 : pageFromSession > 0 ? pageFromSession - 1 : 0);
    setSortBy(isColumnKey(sortByFromSession) ? sortByFromSession : null);
    setSortDirection(
      sortDirectionFromSession === 'asc' || sortDirectionFromSession === 'desc'
        ? sortDirectionFromSession
        : null,
    );
    if (
      rowsPerPageFromSession > 0
      && rowsPerPageFromSession !== rowsPerPage
      && ROWS_PER_PAGE_OPTIONS.includes(rowsPerPageFromSession)
    ) {
      dispatch(
        setPersistedRowsPerPage({
          tableKey: TablePaginationKey.CollectorCases,
          rowsPerPage: rowsPerPageFromSession,
        }),
      );
    }

    if (initialPatientFilter && !incomingPatientPersistedRef.current) {
      incomingPatientPersistedRef.current = true;
      writeSessionTableState(SESSION_STATE_KEY, {
        ...savedState,
        patient_full_name: initialPatientFilter,
        page: 1,
        rowsPerPage: rowsPerPageFromSession > 0 ? rowsPerPageFromSession : rowsPerPage,
      });
    }

    const hydrateRange = (key: DateFilterKey, setter: React.Dispatch<React.SetStateAction<[Date, Date] | null>>) => {
      const startRaw = savedState[`${key}_start`];
      const endRaw = savedState[`${key}_end`];
      const start = startRaw ? moment.utc(startRaw).tz(timezone).toDate() : null;
      const end = endRaw ? moment.utc(endRaw).tz(timezone).toDate() : null;
      if (start && end && !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
        setter([start, end]);
      } else {
        setter(null);
      }
    };

    hydrateRange('created_at', setSelectedRangeCreatedAt);
    hydrateRange('due_date', setSelectedRangeDueDate);
  }, [dispatch, initialPatientFilter, rowsPerPage, savedState, timezone]);

  const commitFilters = useCallback(
    (raw: Record<string, any>) => {
      const cleaned = cleanFilters(raw);
      writeSessionTableState(SESSION_STATE_KEY, {
        ...cleaned,
        page: 1,
        rowsPerPage,
        ...(sortBy ? { sort_by: sortBy } : {}),
        ...(sortDirection ? { sort_direction: sortDirection } : {}),
      });
      setFilterValues(cleaned);
      setTargetPageIndex(0);
    },
    [rowsPerPage, sortBy, sortDirection],
  );

  const handleFilterChange = (key: string, value: any) => {
    commitFilters({ ...filterValues, [key]: value });
  };

  const handleDateRangeFilterChange = (key: string, value: [Date, Date] | null): void => {
    const updated = { ...filterValues };
    if (key === 'created_at') {
      if (value?.[0] && value?.[1]) {
        updated.created_at_start = moment.tz(value[0], timezone).startOf('day').utc().toISOString();
        updated.created_at_end = moment.tz(value[1], timezone).endOf('day').utc().toISOString();
      } else {
        delete updated.created_at_start;
        delete updated.created_at_end;
      }
      setSelectedRangeCreatedAt(value);
    }
    if (key === 'due_date') {
      if (value?.[0] && value?.[1]) {
        updated.due_date_start = moment.tz(value[0], timezone).startOf('day').utc().toISOString();
        updated.due_date_end = moment.tz(value[1], timezone).endOf('day').utc().toISOString();
      } else {
        delete updated.due_date_start;
        delete updated.due_date_end;
      }
      setSelectedRangeDueDate(value);
    }
    commitFilters(updated);
  };

  const params = useMemo(() => {
    const { case_type, care_advisor_full_name, client_division_ids, ...restFilters } = filterValues;
    const apiFilters: Record<string, unknown> = { ...restFilters };
    if (Array.isArray(case_type) && case_type.length > 0) apiFilters.type = case_type;
    if (care_advisor_full_name != null && String(care_advisor_full_name).trim() !== '') {
      apiFilters.care_advisor_full_name_filter = care_advisor_full_name;
    }
    if (Array.isArray(client_division_ids) && client_division_ids.length > 0) {
      apiFilters.client_division_ids = client_division_ids.map(Number);
    }
    return {
      limit: rowsPerPage,
      ...(filterValues.status ? {} : { status: DEFAULT_STATUS_PARAM }),
      ...(sortBy ? { sort_by: SORT_BY_MAP[sortBy] } : {}),
      ...(sortDirection ? { sort_direction: sortDirection } : {}),
      ...apiFilters,
    } as IListCasesParams;
  }, [filterValues, rowsPerPage, sortBy, sortDirection]);

  const {
    cases,
    loadNextPage,
    loadPreviousPage,
    reloadAll,
    dataLoading,
    totalNewCases,
    pageIndex,
    casesListError,
    clearCasesListError,
  } = useCases(params, filtersReady, UserRoleName.Collector, { inlineListErrors: true });

  useEffect(() => {
    if (!filtersReady || dataLoading || pageIndex >= targetPageIndex) return;
    void loadNextPage();
  }, [dataLoading, filtersReady, loadNextPage, pageIndex, targetPageIndex]);

  const columnWidths = useMemo(
    () => buildColumnWidths(cases, userInfo?.role?.name || UserRoleName.Collector, userInfo),
    [cases, userInfo],
  );

  useEffect(() => {
    const sub: Subscriber = {
      commands: ['case_updated', 'case_cancelled', 'case_new_message_created'],
      onMessage: (message: Record<string, any>) => {
        if (['case_updated', 'case_cancelled', 'case_new_message_created'].includes(message.command)) {
          reloadAll();
        }
      },
    };
    const id = subscribe(sub);
    return () => unsubscribe(id);
  }, [reloadAll, subscribe, unsubscribe]);

  const handleSort = (key: string) => {
    const column = key as ColumnKey;
    let nextSortBy: ColumnKey | null = column;
    let nextSortDirection: 'asc' | 'desc' | null = 'asc';
    if (sortBy !== column) {
      setSortBy(column);
      setSortDirection('asc');
    } else if (sortDirection === 'asc') {
      setSortDirection('desc');
      nextSortDirection = 'desc';
    } else {
      setSortBy(null);
      setSortDirection(null);
      nextSortBy = null;
      nextSortDirection = null;
    }
    setTargetPageIndex(0);
    writeSessionTableState(SESSION_STATE_KEY, {
      ...filterValues,
      page: 1,
      rowsPerPage,
      ...(nextSortBy ? { sort_by: nextSortBy } : {}),
      ...(nextSortDirection ? { sort_direction: nextSortDirection } : {}),
    });
  };

  const columns: ColumnDef<ICase>[] = useMemo(() => [
    {
      key: 'created_at',
      label: 'Requested date',
      widthPx: columnWidths.created_at,
      renderCell: (row) => <EllipsisCell text={formatDate(row.created_at)} />,
      filterContent: (
        <MinimalDateRangePicker
          filterKey="created_at"
          selectedRange={selectedRangeCreatedAt}
          handleDateRangeFilterChange={handleDateRangeFilterChange}
          width="100%"
        />
      ),
    },
    {
      key: 'due_date',
      label: 'Due date',
      widthPx: columnWidths.due_date,
      renderCell: (row) => (
        <EllipsisCell
          text={calculateProviderCaseDue(row.type, row.started_at, row.video_conference_date || '', row.due_date)}
        />
      ),
      filterContent: (
        <MinimalDateRangePicker
          filterKey="due_date"
          selectedRange={selectedRangeDueDate}
          handleDateRangeFilterChange={handleDateRangeFilterChange}
          width="100%"
        />
      ),
    },
    {
      key: 'case_type',
      label: 'Case type',
      widthPx: columnWidths.case_type,
      renderCell: (row) => <EllipsisCell text={getFriendlyName(row.type)} />,
      filterContent: (
        <FormControl variant="standard" fullWidth>
          <InputLabel shrink={(filterValues.case_type || []).length > 0} sx={filterLabelSx}>
            Type
          </InputLabel>
          <Select
            multiple
            value={filterValues.case_type || []}
            onChange={(e) => handleFilterChange('case_type', e.target.value)}
            disableUnderline
            sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
            renderValue={(selected) =>
              (selected as string[])
                .map((v) => allCaseTypeOptions.find((o) => o.value === v)?.label)
                .join(', ')
            }
          >
            {allCaseTypeOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                <Checkbox size="small" checked={(filterValues.case_type || []).includes(o.value)} />
                <ListItemText primary={o.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
    },
    {
      key: 'id',
      label: 'Case ID',
      widthPx: columnWidths.id,
      renderCell: (row) => <EllipsisCell text={String(row.id)} />,
      filterContent: (
        <TextField
          variant="standard"
          fullWidth
          label="Case ID"
          value={filterValues.case_id || ''}
          onChange={(e) => handleFilterChange('case_id', e.target.value)}
          InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
          InputLabelProps={{ shrink: Boolean(filterValues.case_id), sx: filterLabelSx }}
        />
      ),
    },
    {
      key: 'patient',
      label: 'Patient',
      widthPx: columnWidths.patient,
      renderCell: (row) => <EllipsisCell text={row.patient?.full_name ?? '-'} />,
      filterContent: (
        <TextField
          variant="standard"
          fullWidth
          label="Patient name"
          value={filterValues.patient_full_name || ''}
          onChange={(e) => handleFilterChange('patient_full_name', e.target.value)}
          InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
          InputLabelProps={{ shrink: Boolean(filterValues.patient_full_name), sx: filterLabelSx }}
        />
      ),
    },
    {
      key: 'provider',
      label: 'Provider',
      widthPx: columnWidths.provider,
      renderCell: (row) => <EllipsisCell text={row.provider?.full_name ?? '-'} />,
      filterContent: (
        <TextField
          variant="standard"
          fullWidth
          label="Provider name"
          value={filterValues.provider_full_name || ''}
          onChange={(e) => handleFilterChange('provider_full_name', e.target.value)}
          InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
          InputLabelProps={{ shrink: Boolean(filterValues.provider_full_name), sx: filterLabelSx }}
        />
      ),
    },
    {
      key: 'care_advisor',
      label: 'Care Advisor',
      widthPx: columnWidths.care_advisor,
      renderCell: (row) => <EllipsisCell text={row.care_advisor?.full_name ?? '-'} />,
      filterContent: (
        <TextField
          variant="standard"
          fullWidth
          label="Care Advisor name"
          value={filterValues.care_advisor_full_name || ''}
          onChange={(e) => handleFilterChange('care_advisor_full_name', e.target.value)}
          InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
          InputLabelProps={{ shrink: Boolean(filterValues.care_advisor_full_name), sx: filterLabelSx }}
        />
      ),
    },
    {
      key: 'collector',
      label: 'Collector',
      widthPx: columnWidths.collector,
      renderCell: (row) => (
        <EllipsisCell text={userInfo ? getCollectorDisplayName(row, userInfo) : '-'} />
      ),
      filterContent: (
        <TextField
          variant="standard"
          fullWidth
          label="Collector name"
          value={filterValues.collector_full_name || ''}
          onChange={(e) => handleFilterChange('collector_full_name', e.target.value)}
          InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
          InputLabelProps={{ shrink: Boolean(filterValues.collector_full_name), sx: filterLabelSx }}
        />
      ),
    },
    {
      key: 'client_tag',
      label: 'Client tags',
      widthPx: columnWidths.client_tag,
      renderCell: (row) => <EllipsisCell text={row.client?.name ?? '-'} />,
      filterContent: (
        <FormControl variant="standard" fullWidth>
          <InputLabel shrink={((filterValues.client_tag as string[]) || []).length > 0} sx={filterLabelSx}>
            Client tags
          </InputLabel>
          <Select
            multiple
            value={(filterValues.client_tag as string[]) || []}
            onChange={(e) =>
              commitFilters({ ...filterValues, client_tag: e.target.value as string[], client_division_ids: undefined })
            }
            disableUnderline
            sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
            renderValue={(selected) =>
              (selected as string[]).map((v) => clientTagOptions.find((o) => o.value === v)?.label).join(', ')
            }
          >
            {clientTagOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                <Checkbox size="small" checked={((filterValues.client_tag as string[]) || []).includes(o.value)} />
                <ListItemText primary={o.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
    },
    {
      key: 'client_division',
      label: 'Division',
      widthPx: columnWidths.client_division,
      sortable: false,
      renderCell: (row) => <EllipsisCell text={row.client_division?.name ?? '-'} />,
      filterContent: (
        <FormControl variant="standard" fullWidth>
          <InputLabel shrink={((filterValues.client_division_ids as string[]) || []).length > 0} sx={filterLabelSx}>
            {(filterValues.client_tag as string[])?.length ? 'Division' : 'Select a client tag first'}
          </InputLabel>
          <Select
            multiple
            disabled={!(filterValues.client_tag as string[])?.length}
            value={(filterValues.client_division_ids as string[]) || []}
            onChange={(e) => handleFilterChange('client_division_ids', e.target.value)}
            disableUnderline
            sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
            renderValue={(selected) =>
              (selected as string[])
                .map((v) => divisionFilterOptions.find((o) => o.id === v)?.label)
                .join(', ')
            }
          >
            {divisionFilterOptions.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                <Checkbox size="small" checked={((filterValues.client_division_ids as string[]) || []).includes(o.id)} />
                <ListItemText primary={o.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      widthPx: columnWidths.status,
      renderCell: (row) => {
        const statusText = getTextForStatus(userInfo.role.name, row.type, row.status);
        const statusStyle = getStatusColorStyles(row.status);
        return (
          <Tooltip title={statusText} placement="top-start" enterDelay={400}>
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  maxWidth: '100%',
                  px: 1.25,
                  py: 0.5,
                  borderRadius: '999px',
                  fontSize: 12,
                  fontWeight: 500,
                  lineHeight: 1.2,
                  color: statusStyle.color,
                  backgroundColor: statusStyle.backgroundColor,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {statusText}
              </Box>
            </Box>
          </Tooltip>
        );
      },
      filterContent: (
        <FormControl variant="standard" fullWidth>
          <InputLabel shrink={(filterValues.status || []).length > 0} sx={filterLabelSx}>
            Status
          </InputLabel>
          <Select
            multiple
            value={filterValues.status || []}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            disableUnderline
            sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
            renderValue={(selected) =>
              (selected as string[]).map((v) => caseStatusOptions.find((o) => o.value === v)?.label).join(', ')
            }
          >
            {caseStatusOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                <Checkbox size="small" checked={(filterValues.status || []).includes(o.value)} />
                <ListItemText primary={o.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
    },
  ], [
    columnWidths,
    filterValues,
    selectedRangeCreatedAt,
    selectedRangeDueDate,
    userInfo,
    clientTagOptions,
    divisionFilterOptions,
    commitFilters,
  ]);

  if ((dataLoading && cases.length === 0) || !filtersReady) {
    return <Loading dotted />;
  }

  return (
    <div style={{ width: '100%' }}>
      <div className={styles.providerDashboarTableHeader}>
        <div className={styles.providerDashboarTableHeaderTop}>
          {/* The design marks this page heading up as an h1, as the care
              advisor's identical Cases heading already does here. It rendered
              at the right size and colour but as a span, so it was not a
              heading to a screen reader. */}
          <h1 className={styles.providerDashboarTableHeaderText}>Cases</h1>
          <Button
            variant="contained"
            onClick={() => navigate(`${COLLECTOR_CASE_CREATION_BASE_PATH}/patient/select`)}
            size="medium"
            className={styles.collectorPrimaryPillButton}
            startIcon={<AddIcon />}
          >
            Create new case
          </Button>
        </div>
      </div>

      <CADataGrid
        columns={columns}
        rows={cases}
        onRowClick={(row) => {
          sessionStorage.setItem('lastNonConsultationPagePath', `${location.pathname}${location.hash}`);
          if (row.type === CaseType.smile_scan && row.status === Status.Completed) {
            roleNavigate(`/smile-scan-report/${row.id}`);
          } else {
            roleNavigate(`/consultation-page/${row.id}`);
          }
        }}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSort={handleSort}
        count={totalNewCases}
        page={pageIndex}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        onPageChange={(nextPage) => {
          if (nextPage > pageIndex) loadNextPage();
          else if (nextPage < pageIndex) loadPreviousPage();
          setTargetPageIndex(nextPage);
          writeSessionTableState(SESSION_STATE_KEY, {
            ...filterValues,
            page: nextPage + 1,
            rowsPerPage,
            ...(sortBy ? { sort_by: sortBy } : {}),
            ...(sortDirection ? { sort_direction: sortDirection } : {}),
          });
        }}
        onRowsPerPageChange={(rpp) => {
          dispatch(
            setPersistedRowsPerPage({ tableKey: TablePaginationKey.CollectorCases, rowsPerPage: rpp }),
          );
          setTargetPageIndex(0);
          writeSessionTableState(SESSION_STATE_KEY, {
            ...filterValues,
            page: 1,
            rowsPerPage: rpp,
            ...(sortBy ? { sort_by: sortBy } : {}),
            ...(sortDirection ? { sort_direction: sortDirection } : {}),
          });
        }}
        loading={dataLoading}
        error={casesListError as 'forbidden' | 'generic' | null}
        onRetry={() => { clearCasesListError(); void reloadAll(); }}
        isMobile={isMobile}
        showManageColumns
        columnVisibilityKey={ColumnVisibilityKey.CollectorCases}
      />
    </div>
  );
};

export default Appointments;
